import {
  BID_REQUEST_STATUS_ENUM,
  BidAmlStatusCheckResponse,
  CONTRACT_CURRENCY_ENUM,
  CONTRACT_STATUS_ENUM,
  FIAT_CURRENCY_ENUM,
  DmnMessageDto,
  PROCESSING_TYPE,
} from "interfaces";
import { BID_REQUEST_TYPE_ENUM, BidEntity } from "interfaces/bids";
import { makeAutoObservable, runInAction, reaction, comparer } from "mobx";
import {
  getAmlCheckStatus,
  getBidById,
  getDmnTooltips,
  requestCustomerExecuted,
  requestNominalFundsWithdrawal,
  updateCurrencyOperationCode,
} from "services";
import { amlApproveRiskAll } from "services/amlService";
import { NormalizedError } from "services/api/errorHandler";
import { statusFetching } from "services/authClient/constants/types";
import { DIRECT_PAYMENT_CUSTOMER_LP } from "utils/envConstants";
import { fetchStatus, withRequestTracking } from "utils/storesUtils";

const defaultBidEntity: BidEntity = {
  id: "",
  amount: 0,
  currency: CONTRACT_CURRENCY_ENUM.USDT,
  currencyOperationCode: "",
  counterpartyCountryCode: "",
  documents: {},
  fiatAmount: 0,
  fiatCurrency: FIAT_CURRENCY_ENUM.USD,
  customerFee: 0,
  lpFee: 0,
  extraFee: 0,
  transitWallet: "",
  status: BID_REQUEST_STATUS_ENUM.DRAFT,
  statusDate: "",
  created: "",
  network: undefined,
  contract: {
    id: "",
    companyId: "",
    customerRegistrationNumber: "",
    direction: "EXP",
    companyName: "",
    unk: "",
    status: CONTRACT_STATUS_ENUM.DRAFT,
  },
  quote: {
    id: "",
    rate: 0,
    feeCurRate: 0,
    lpCompanyName: "",
    lpCompRegNum: "",
    lpId: "",
  },
  amountRub: 0,
  networkFee: 0,

  destinationWallet: "",
  ethAmlRisk: false,
  hid: 0,
  reports: [],
  pendingOutgoingTx: false,
  balance: {
    token: "",
    native: "",
  },
  type: BID_REQUEST_TYPE_ENUM.REQUEST_WITH_AGENT,
  additionalDocuments: {},
  counterpartyCountry: "",
  counterpartyName: "",
  requestProcessing: {
    fiatSent: false,
    fiatReceived: false,
    tokenReceived: false,
    nativeReceived: false,
    rubReceivedByPlatform: false,
    fiatWithdrawalRequested: false,
    customerExecuted: false,
    feeRubReceivedByPlatform: false,
    destinationWalletVerified: false,
    outgoingTxFailed: false,
  },
  splitCount: 1,
  transactionCount: 1,
  walletChecksCount: 1,
  collectQuotesImmediately: true,
  rubBalance: 0,
  customerRegistrationNumber: "",
  destinationWalletVerified: false,
  quoteDestinationWalletVerified: false,
  userId: "",
};

const defaultStateStatus = { loading: false, fetchStatus: fetchStatus.idle, fetchError: undefined };

export class BidCardStore {
  public bid: BidEntity = { ...defaultBidEntity };
  public fetchStatus: statusFetching = "init";
  public fetchError?: NormalizedError;
  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  approveAllStatuses = { ...defaultStateStatus };
  public amlCheckStatusData: BidAmlStatusCheckResponse[] | null = null;
  amlCheckStatuses = { ...defaultStateStatus };
  public dmnTooltipsData: DmnMessageDto[] = [];
  dmnTooltipsStatuses = { ...defaultStateStatus };
  public activeTab?: string;
  requestNominalFundsWithdrawalStatuses = { ...defaultStateStatus };
  requestCustomerExecutedStatuses = { ...defaultStateStatus };
  updateCurrencyOperationCodeStatuses = { ...defaultStateStatus };

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.bid,
      () => {
        if (this.bid.id) {
          this.fetchDmnTooltips(this.bid.id);
        }
      }
    );
  }

  get isPending() {
    return this.fetchStatus === "fetching";
  }

  stopPolling: () => void = () => {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  };

  startPolling = async (id: string, condition: (data: BidEntity) => boolean, onFinish?: () => void) => {
    const pollInterval = 1000;
    let requestCount = 0;

    const fetchAndCheckCondition = async () => {
      if (requestCount >= 5) {
        this.stopPolling();
        return;
      }
      await getBidById({ id })
        .then((data) => {
          if (condition(data)) {
            runInAction(() => {
              this.bid = data;
            });
            this.stopPolling();

            if (onFinish) {
              onFinish();
            }
          }
        })
        .catch((e) => {
          runInAction(() => {
            this.fetchStatus = "error";
            this.fetchError = e;
          });
          this.stopPolling();
          return;
        })
        .finally(() => requestCount++);
    };

    await fetchAndCheckCondition();
    this.pollingInterval = setInterval(fetchAndCheckCondition, pollInterval);

    return () => {
      this.stopPolling();
    };
  };

  fetchCardById = async (id: string) => {
    runInAction(() => {
      this.fetchStatus = "fetching";
      this.fetchError = undefined;
    });

    return getBidById({ id })
      .then((res) => {
        runInAction(() => {
          this.bid = res;
          this.fetchStatus = "success";
        });
      })
      .catch((error: NormalizedError) => {
        runInAction(() => {
          this.fetchStatus = "error";
          this.fetchError = error;
        });
        throw error;
      });
  };

  updateCurrentCard = async () => {
    return this.bid.id && (await this.updateCardById(this.bid.id));
  };

  updateCardById = async (id: string, type?: string) => {
    return getBidById({ id })
      .then((res) => {
        runInAction(() => {
          this.bid = res;
        });
      })
      .catch((error: NormalizedError) => {
        runInAction(() => {
          this.fetchStatus = "error";
          this.fetchError = error;
        });
      });
  };

  async approveAllRisks(requestId: string) {
    return withRequestTracking(this.approveAllStatuses, async () => {
      await amlApproveRiskAll({ requestId: requestId });
    });
  }

  async fetchAmlCheckStatus(id: string) {
    return withRequestTracking(this.amlCheckStatuses, async () => {
      const res = await getAmlCheckStatus({ requestId: id });
      this.amlCheckStatusData = res;
    });
  }

  async fetchDmnTooltips(id: string = this.bid.id) {
    return withRequestTracking(this.dmnTooltipsStatuses, async () => {
      this.dmnTooltipsData = await getDmnTooltips({ id });
    });
  }

  async fetchRequestNominalFundsWithdrawal(id: string, formData: any) {
    return withRequestTracking(this.requestNominalFundsWithdrawalStatuses, async () => {
      await requestNominalFundsWithdrawal({ id }, formData);
    });
  }

  async requestCustomerExecuted(id: string) {
    return withRequestTracking(this.requestCustomerExecutedStatuses, async () => {
      await requestCustomerExecuted({ id });
    });
  }

  setActiveTab(newTab: string) {
    this.activeTab = newTab;
  }
  async updateCurrencyOperationCode(id: string, formData: any) {
    return withRequestTracking(this.updateCurrencyOperationCodeStatuses, async () => {
      await updateCurrencyOperationCode({ id }, formData);
    });
  }

  clear() {
    this.bid = { ...defaultBidEntity };
    this.fetchStatus = "init";
    this.fetchError = undefined;
    this.dmnTooltipsData = [];
    this.approveAllStatuses = { ...defaultStateStatus };
    this.amlCheckStatuses = { ...defaultStateStatus };
    this.dmnTooltipsStatuses = { ...defaultStateStatus };
    this.activeTab = undefined;
  }
}
